import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import DialogContent from "@mui/material/DialogContent";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { loadAlbums, fetchAlbumActivity } from "../../../albumsApi";
import { formatRelativeTime } from "../../../utils";
import { mediaUrl } from "../../../mediaAuth";
import { AvatarImage } from "../../components/AvatarImage";
import { AppDialog } from "../../components/AppDialog";
import { AppDialogHeader } from "../../components/AppDialogHeader";
import { firstNameOf } from "../home/CoupleHeader";
import { AlbumCard } from "./AlbumCard";
import { CreateAlbumDialog } from "./CreateAlbumDialog";
import { AlbumOptionsDialog } from "./AlbumOptionsDialog";
import { AlbumTagsDialog } from "./AlbumTagsDialog";
import type { Album, AlbumActivityEntry } from "../../../types";

const MAX_ACTIVITY_ROWS = 3;

function initialOf(name: string): string {
  return (name || "?").trim().charAt(0).toUpperCase();
}

// A light, single-row identity strip - NOT a copy of Home's CoupleHeader
// hero (no greeting, no decorative hearts, no multi-line layout). The
// notification/profile/theme buttons the concept mockup shows next to it
// are already the persistent global header (public/index.html) shown on
// every route including this one, so they don't get duplicated here - this
// only needs to answer "whose albums are these," compactly. The privacy
// statement lives here too now (a "Private memories" subtitle under the
// name) instead of as a separate paragraph below the Albums heading - one
// compact two-line block instead of two, so real content (the album
// carousel) still appears immediately under it.
function AlbumsIdentity() {
  const presenceUsers = useAppState((s) => s.presenceUsers);
  const myEmail = useAppState((s) => s.currentUserEmail);
  const myFullName = useAppState((s) => s.currentUserFullName);
  const myAvatarFilename = useAppState((s) => s.currentUserAvatarFilename);

  const partner = presenceUsers[0] ?? null;
  const myName = firstNameOf(myFullName, myEmail);
  const partnerName = partner ? firstNameOf(partner.fullName, partner.email) : "";

  return (
    <div className="albums-identity">
      <div className="albums-identity-avatars">
        <span className="albums-identity-avatar">
          {myAvatarFilename ? (
            <AvatarImage
              filename={myAvatarFilename}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
              fallback={initialOf(myName)}
            />
          ) : (
            initialOf(myName)
          )}
        </span>
        {partner && (
          <span className="albums-identity-avatar">
            {partner.avatarFilename ? (
              <AvatarImage
                filename={partner.avatarFilename}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                fallback={initialOf(partnerName)}
              />
            ) : (
              initialOf(partnerName)
            )}
            <span className={`albums-identity-status-dot${partner.online ? " is-online" : ""}`} aria-hidden="true" />
          </span>
        )}
      </div>
      <span className="albums-identity-text">
        <span className="albums-identity-name">
          {myName || "You"}
          {partner && <> &amp; {partnerName}</>}
        </span>
        {/* Not a claim of end-to-end encryption - media IS encrypted at
            rest (AES-256), but the server itself holds the key, which
            isn't E2E in the cryptographic sense. Every album here is
            uniformly private (shared only between the two authenticated
            accounts on this app instance), so this is a true statement
            regardless of which album is in view. */}
        <span className="albums-identity-sub">
          <i className="bi bi-lock-fill" aria-hidden="true" /> Private memories
        </span>
      </span>
    </div>
  );
}

// Real-data shortcut (appState.favoritesSet, already loaded app-wide by
// AuthGate.tsx) - deliberately the ONLY quick-shortcut shown, since it's
// the one genuinely backed by real data. Selfies/Trips/Friends/Events from
// the original concept stay omitted - no tag/people/location detection
// exists in this app (see the previous phase's report). Hidden entirely
// when there are zero favorites, rather than inviting a tap into an empty
// gallery view.
function FavoritesShortcut() {
  const navigate = useNavigate();
  const favoritesSet = useAppState((s) => s.favoritesSet);
  if (!favoritesSet.size) return null;

  function open(): void {
    appState.favoritesOnly = true;
    notifyStateChange();
    navigate("/gallery");
  }

  const countLabel = `${favoritesSet.size} ${favoritesSet.size === 1 ? "memory" : "memories"}`;

  return (
    <button type="button" className="albums-shortcut" onClick={open} aria-label={`Favorites, ${countLabel}`}>
      <span className="albums-shortcut-icon" aria-hidden="true">
        <i className="bi bi-heart-fill" aria-hidden="true" />
      </span>
      <span className="albums-shortcut-text" aria-hidden="true">
        <span className="albums-shortcut-title">Favorites</span>
        <span className="albums-shortcut-count">&middot; {countLabel}</span>
      </span>
      <i className="bi bi-chevron-right albums-shortcut-chevron" aria-hidden="true" />
    </button>
  );
}

function AlbumsCarouselSkeleton() {
  return (
    <div className="albums-carousel">
      {Array.from({ length: 2 }).map((_, i) => (
        <div className="albums-carousel-item" key={i}>
          <div className="albums-skeleton-cover" />
          <div className="albums-skeleton-line" style={{ width: "60%", marginTop: 10 }} />
          <div className="albums-skeleton-line" style={{ width: "40%", marginTop: 8 }} />
        </div>
      ))}
    </div>
  );
}

function AlbumsEmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="albums-empty">
      <i className="bi bi-heart albums-empty-icon" aria-hidden="true" />
      <div className="albums-empty-title">Your memories start here</div>
      <p className="albums-empty-copy">Create your first shared album for the photos and videos you want to keep together.</p>
      <button type="button" className="btn btn-outline-pink btn-sm" onClick={onCreate}>
        <i className="bi bi-plus-lg me-1" aria-hidden="true" /> Create Album
      </button>
    </div>
  );
}

// Featured album carousel: ~82% viewport width per card (next card peeks
// to invite a swipe), scroll-snap, with a small dot strip tracking
// scroll position - same idiom as Home's ActivityCarousel.tsx, duplicated
// here rather than shared/extracted so this pass never has to touch
// Home's own files.
function AlbumsCarousel({ albums, onOptions }: { albums: Album[]; onOptions: (albumId: string) => void }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const rafRef = useRef<number | null>(null);

  const handleScroll = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const track = trackRef.current;
      if (!track) return;
      let closest = 0;
      let closestDist = Infinity;
      itemRefs.current.forEach((el, i) => {
        if (!el) return;
        const dist = Math.abs(el.offsetLeft - track.scrollLeft);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });
      setActiveIndex(closest);
    });
  }, []);

  function goTo(index: number): void {
    itemRefs.current[index]?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  }

  // A single album at the normal ~82% carousel width just sits at the left
  // edge with dead space to its right and nothing to swipe to - not the
  // "next card peeks" layout that width is meant for. Widened + the track
  // centered so it reads as an intentional single-card layout instead.
  const isSingle = albums.length === 1;

  return (
    <>
      <div
        className={`albums-carousel${isSingle ? " is-single" : ""}`}
        ref={trackRef}
        onScroll={handleScroll}
        role="list"
        aria-label="Albums"
      >
        {albums.map((album, i) => (
          <div
            className={`albums-carousel-item${isSingle ? " is-single" : ""}`}
            role="listitem"
            key={album.id}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
          >
            <AlbumCard album={album} onOptions={onOptions} />
          </div>
        ))}
      </div>
      {albums.length > 1 && (
        <div className="albums-carousel-dots" role="tablist" aria-label="Album carousel position">
          {albums.map((album, i) => (
            <button
              key={album.id}
              type="button"
              className={`albums-carousel-dot${i === activeIndex ? " is-active" : ""}`}
              role="tab"
              aria-selected={i === activeIndex}
              aria-label={`Show ${album.name}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}
    </>
  );
}

// Action+target prose built client-side from the server's structured event
// data (type/album/summary) - the server deliberately never sends
// pre-written sentences (see GET /api/albums/activity's own comment). The
// actor's name is shown separately (its own bold line in the row), so this
// only covers "what happened, to what" - e.g. "Added 8 photos to ORORA".
function activityActionLabel(entry: AlbumActivityEntry): string {
  const s = entry.summary;
  const album = entry.album.name;
  switch (entry.type) {
    case "album_created":
      return `Created ${album}`;
    case "album_renamed":
      return `Renamed the album to ${album}`;
    case "media_uploaded": {
      const photos = s?.imageCount || 0;
      const videos = s?.videoCount || 0;
      if (photos && videos) return `Added ${photos} photo${photos > 1 ? "s" : ""} and ${videos} video${videos > 1 ? "s" : ""} to ${album}`;
      if (videos) return `Added ${videos} video${videos > 1 ? "s" : ""} to ${album}`;
      return `Added ${photos} photo${photos > 1 ? "s" : ""} to ${album}`;
    }
    case "media_deleted":
      return `Removed a photo or video from ${album}`;
    case "comment_added":
      return `Commented in ${album}`;
    case "comment_deleted":
      return `Deleted a comment in ${album}`;
    case "album_tag_added":
      return `Tagged ${album} "${s?.tag || ""}"`;
    case "album_tag_removed":
      return `Removed the "${s?.tag || ""}" tag from ${album}`;
    default:
      return `Made a change to ${album}`;
  }
}

// media_favorited/unfavorited are real events (see server.js) but would be
// noisy in a feed that's otherwise about albums/uploads/comments - kept
// out of the main feed for now while still being fetched/retained
// server-side for future use (see the activity spec's own PART 12).
const HIDDEN_FROM_FEED = new Set(["media_favorited", "media_unfavorited"]);

function ActivityRow({ entry, onClick }: { entry: AlbumActivityEntry; onClick: () => void }) {
  return (
    <button type="button" className="albums-activity-row" onClick={onClick}>
      <span className="albums-activity-thumb-wrap">
        <span className="albums-activity-thumb">
          {entry.album.cover ? (
            <img src={mediaUrl(`/assets/thumbs/${entry.album.cover}`)} alt="" loading="lazy" />
          ) : (
            <i className="bi bi-folder2-open" aria-hidden="true" />
          )}
        </span>
        {/* Real actor avatar only - no initials/placeholder fallback here,
            since a badge with a placeholder would claim more identity
            confidence than the data actually supports. The actor's name
            is already the row's bold first line, so this is a subtle
            reinforcement, not the primary "who" signal. */}
        {entry.actor?.avatarFilename && (
          <span className="albums-activity-actor-badge" aria-hidden="true">
            <AvatarImage
              filename={entry.actor.avatarFilename}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
              fallback=""
            />
          </span>
        )}
      </span>
      <span className="albums-activity-body">
        <span className="albums-activity-actor text-truncate">{entry.actor?.displayName || "Someone"}</span>
        <span className="albums-activity-detail text-truncate">{activityActionLabel(entry)}</span>
      </span>
      {entry.createdAt && <span className="albums-activity-time">{formatRelativeTime(entry.createdAt)}</span>}
    </button>
  );
}

// "View all" reuses the exact same real activity API at a higher limit
// rather than a dedicated activity page/route - no new backend surface,
// just more of the same data in a dialog.
function AllActivityDialog({ open, onClose, onSelectAlbum }: { open: boolean; onClose: () => void; onSelectAlbum: (albumId: string) => void }) {
  const [entries, setEntries] = useState<AlbumActivityEntry[] | null>(null);

  useEffect(() => {
    if (!open) return;
    setEntries(null);
    let cancelled = false;
    fetchAlbumActivity(50).then((result) => {
      if (!cancelled) setEntries(result);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;
  const visible = (entries || []).filter((e) => !HIDDEN_FROM_FEED.has(e.type));

  return (
    <AppDialog open onClose={onClose} maxWidth="xs" fullWidth>
      <AppDialogHeader icon="bi-activity" title="Recent Activity" onBack={onClose} />
      <DialogContent sx={{ p: 3, pt: 1 }}>
        {!entries ? (
          <p className="small muted text-center mb-0">Loading…</p>
        ) : !visible.length ? (
          <p className="small muted text-center mb-0">No activity yet.</p>
        ) : (
          <div className="albums-activity-list">
            {visible.map((entry) => (
              <ActivityRow key={entry.id} entry={entry} onClick={() => onSelectAlbum(entry.album.id)} />
            ))}
          </div>
        )}
      </DialogContent>
    </AppDialog>
  );
}

function RecentActivity({ enabled }: { enabled: boolean }) {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<AlbumActivityEntry[] | null>(null);
  const [viewAllOpen, setViewAllOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    fetchAlbumActivity(20).then((result) => {
      if (!cancelled) setEntries(result);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  function openAlbum(albumId: string): void {
    setViewAllOpen(false);
    navigate(`/albums/${albumId}`);
  }

  // null = still loading or the request failed - either way, say nothing
  // rather than a false empty state (a failed activity fetch must not make
  // the rest of Albums look broken or empty).
  if (!entries) return null;
  const all = entries.filter((e) => !HIDDEN_FROM_FEED.has(e.type));
  if (!all.length) return null;
  const visible = all.slice(0, MAX_ACTIVITY_ROWS);

  return (
    <div className="albums-activity">
      <div className="home-section-heading">
        <h2 className="home-section-title">Recent Activity</h2>
        {all.length > MAX_ACTIVITY_ROWS && (
          <button type="button" className="home-see-all" onClick={() => setViewAllOpen(true)}>
            View all <i className="bi bi-chevron-right" aria-hidden="true" />
          </button>
        )}
      </div>
      <div className="albums-activity-list">
        {visible.map((entry) => (
          <ActivityRow key={entry.id} entry={entry} onClick={() => openAlbum(entry.album.id)} />
        ))}
      </div>
      <AllActivityDialog open={viewAllOpen} onClose={() => setViewAllOpen(false)} onSelectAlbum={openAlbum} />
    </div>
  );
}

function AlbumsGrid() {
  // useSyncExternalStore compares snapshots with Object.is - a selector
  // that returns a fresh array every call (like .filter() directly) looks
  // "always changed," causing an infinite render loop. Read the raw stable
  // reference and derive with useMemo instead.
  const allAlbums = useAppState((s) => s.albums);
  const albums = useMemo(() => allAlbums.filter((a) => !a.isDefault), [allAlbums]);
  const [loaded, setLoaded] = useState(false);
  const [optionsForId, setOptionsForId] = useState<string | null>(null);
  const [tagsForId, setTagsForId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    loadAlbums().finally(() => setLoaded(true));
  }, []);

  const optionsAlbum = albums.find((a) => a.id === optionsForId) || null;
  const tagsAlbum = albums.find((a) => a.id === tagsForId) || null;

  return (
    <>
      {/* Same ambient-glow technique Home's .couple-hero uses (own class,
          Home's file untouched) - gives the top of Albums the same subtle
          warmth Home opens with instead of a flatter background. */}
      <div className="albums-ambient-glow">
        <AlbumsIdentity />

        <div className="home-section-heading">
          <h2 className="home-section-title">
            <i className="bi bi-folder-fill me-2 text-pink" aria-hidden="true" />
            Albums
          </h2>
          <button type="button" className="btn btn-outline-pink albums-new-btn flex-shrink-0" onClick={() => setCreateOpen(true)}>
            <i className="bi bi-plus-lg me-1" aria-hidden="true" /> New Album
          </button>
        </div>
      </div>

      {!loaded && !albums.length ? (
        <AlbumsCarouselSkeleton />
      ) : !albums.length ? (
        <AlbumsEmptyState onCreate={() => setCreateOpen(true)} />
      ) : (
        <>
          <AlbumsCarousel albums={albums} onOptions={setOptionsForId} />
          <FavoritesShortcut />
          <RecentActivity enabled={loaded} />
        </>
      )}

      <CreateAlbumDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <AlbumOptionsDialog album={optionsAlbum} onClose={() => setOptionsForId(null)} onManageTags={setTagsForId} />
      <AlbumTagsDialog album={tagsAlbum} onClose={() => setTagsForId(null)} />
    </>
  );
}

// Portals directly into #albumsListView (previously split between a static
// vanilla heading/button and a nested #albumsContainer grid - folded into
// one, same reasoning as ChatListScreen.tsx's equivalent consolidation).
// The section's own visibility is still route-driven (AlbumsRouteScreen.tsx).
export function AlbumsListScreen() {
  const container = getVanillaEl<HTMLDivElement>("albumsListView");
  if (!container) return null;
  return createPortal(<AlbumsGrid />, container);
}
